// The arithmetic behind the Sunday School summary. Pure functions over the
// rows the screen already holds — scoped to the year being viewed and, for a
// teacher, to their own sections — so the Summary tab only lays answers out.
// The attendance, offering and exam rules themselves live in ./registers and
// are reused here, so the Summary never disagrees with the other tabs.

import { lastMonths, inMonth, sumOf, dateOf, isoOf } from '../../utils/summaryStats';
import type { MonthBucket } from '../../utils/summaryStats';
import {
  classAttendance, studentAttendance, overallAttendance, LOW_ATTENDANCE_PCT,
  examAppliesTo, marksFor, tallyResults, lastSunday, shiftSunday
} from './registers';
import type { RegisterLike, AttendanceRate, ExamLike, MarksRowLike, SectionLike } from './registers';

/** A section with the label every dropdown uses ("Grade 1 – A") and its grade's name. */
export interface SsSectionLike extends SectionLike {
  label: string;
  /** Blank for a class saved before grades existed; it counts as a grade of its own. */
  gradeName?: string;
}

export interface SsStudentLike {
  /** The STU-xxxx id attendance and marks are keyed by. */
  id?: string;
  name?: string;
  classId?: string;
  parentName?: string;
  phone?: string;
}

export interface SsTeacherLike {
  name?: string;
  classId?: string;
}

/** One raw offering row: blank studentId is the section's total, set is one child's gift. */
export interface SsOfferingLike {
  date?: string;
  classId?: string;
  studentId?: string;
  offering?: unknown;
}

export interface SsExamLike extends ExamLike {
  name?: string;
  /** The paper within the exam (Bible Knowledge…); shown after the name. */
  subject?: string;
  date?: string;
  totalMarks?: unknown;
  passMarks?: unknown;
}

/** The academic year being viewed — its dates bound "this term". */
export interface SsTerm {
  name?: string;
  startDate?: string;
  endDate?: string;
}

export interface SsSummaryInput {
  sections: SsSectionLike[];
  students: SsStudentLike[];
  teachers: SsTeacherLike[];
  /** Weekly registers; offering-only rows (no attendance) are ignored for attendance. */
  registers: RegisterLike[];
  offerings: SsOfferingLike[];
  exams: SsExamLike[];
  marks: MarksRowLike[];
  term?: SsTerm | null;
}

export interface SectionReport {
  id: string;
  label: string;
  gradeName: string;
  students: number;
  teachers: string;
  /** Sundays with an attendance sheet. */
  registers: number;
  lastRegister?: string;
  attendance: AttendanceRate;
  offerings: number;
  results: { passed: number; sat: number; passPct: number | null };
  /** Average mark as a share of the total, over every paper sat in this section. */
  avgPct: number | null;
}

export interface GradeReport {
  name: string;
  sections: number;
  students: number;
  withoutTeacher: number;
}

export interface StudentAttendanceRow {
  id: string;
  name: string;
  className: string;
  parentName?: string;
  phone?: string;
  attendance: AttendanceRate;
}

/** A month of attendance: `count` is sheets marked, `pct` heads present of heads marked. */
export interface AttendanceMonth extends MonthBucket {
  present: number;
  marked: number;
  pct: number | null;
}

export interface ExamReport {
  id: string;
  name: string;
  date?: string;
  /** Sections that sit it, and how many of those have marks saved. */
  sections: number;
  entered: number;
  passed: number;
  failed: number;
  absent: number;
  sat: number;
  passPct: number | null;
  avgPct: number | null;
}

export interface TopScorer {
  id: string;
  name: string;
  className: string;
  exams: number;
  obtained: number;
  total: number;
  pct: number;
}

export interface SundaySchoolSummary {
  students: number;
  sections: number;
  teachers: number;
  sectionsWithoutTeacher: SectionReport[];
  overall: AttendanceRate;
  /** The last four Sundays (newest first) and the attendance over them. */
  last4Sundays: string[];
  last4: AttendanceRate;
  /** The four before those — the trend the tile compares against. */
  prev4: AttendanceRate;
  /** Sections with students but no sheet on the most recent Sunday. */
  notMarked: SectionReport[];
  lastSunday: string;
  attendanceMonths: AttendanceMonth[];
  byGrade: GradeReport[];
  bySection: SectionReport[];
  /** Every student with at least one Sunday marked, lowest attendance first. */
  byStudent: StudentAttendanceRow[];
  lowAttendance: StudentAttendanceRow[];
  offeringsTotal: number;
  offeringsThisMonth: number;
  offeringsTerm: number;
  termLabel: string;
  offeringMonths: MonthBucket[];
  /** Split of the money: section totals vs individual children's gifts. */
  classTotals: number;
  personalGifts: number;
  exams: ExamReport[];
  examPassed: number;
  examSat: number;
  examPassPct: number | null;
  examAvgPct: number | null;
  topScorers: TopScorer[];
}

/** One decimal place, as the rest of the screen prints percentages. */
const pct1 = (part: number, whole: number): number | null =>
  whole > 0 ? Math.round((part / whole) * 1000) / 10 : null;

/** A stored ISO date as a local Date (null when it is not one). */
const toDate = (v: unknown): Date | null => {
  const d = dateOf(v);
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
};

const hasSheet = (r: RegisterLike) => Object.keys(r.attendance || {}).length > 0;

/** A mark that was actually written down — blank is "did not sit", never zero. */
const markOf = (v: unknown): number | null => {
  const raw = String(v ?? '').trim();
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Every figure on the Summary tab. `now` is fixed in tests. Trends are drawn
 * up to the end of the term being viewed when that term is already over, so
 * looking back at last year does not show twelve empty months.
 */
export const sundaySchoolSummary = (input: SsSummaryInput, now: Date = new Date()): SundaySchoolSummary => {
  const { sections, students, teachers, registers, offerings, exams, marks, term } = input;
  const termEnd = toDate(term?.endDate);
  const asOf = termEnd && isoOf(termEnd) < isoOf(now) ? termEnd : now;
  const sheets = registers.filter(hasSheet);
  const sectionById = new Map(sections.map((s) => [String(s.id), s]));
  const rollOf = (classId: string) => students.filter((st) => st.classId === classId);

  // --- Exams: one pass over exam x section, feeding three tables -----------
  const examRows: ExamReport[] = [];
  const perSection = new Map<string, { passed: number; sat: number; obtained: number; total: number }>();
  const perStudent = new Map<string, { exams: number; obtained: number; total: number }>();
  exams.forEach((x) => {
    const report: ExamReport = {
      id: String(x.id || ''), name: [x.name, x.subject].filter((v) => String(v || '').trim()).join(' · ') || '-', date: x.date,
      sections: 0, entered: 0, passed: 0, failed: 0, absent: 0, sat: 0, passPct: null, avgPct: null
    };
    const outOf = Number(x.totalMarks) || 0;
    let obtained = 0;
    sections.filter((c) => examAppliesTo(x, c)).forEach((c) => {
      report.sections += 1;
      const row = marksFor(marks, x, String(c.id));
      if (!row) return;
      report.entered += 1;
      const roll = rollOf(String(c.id));
      const tally = tallyResults(roll.map((st) => String(st.id)), row.scores, x);
      report.passed += tally.passed; report.failed += tally.failed; report.absent += tally.absent;
      const sec = perSection.get(String(c.id)) || { passed: 0, sat: 0, obtained: 0, total: 0 };
      sec.passed += tally.passed; sec.sat += tally.sat;
      roll.forEach((st) => {
        const got = markOf(row.scores?.[String(st.id)]?.obtained);
        if (got === null) return;
        obtained += got;
        sec.obtained += got; sec.total += outOf;
        const mine = perStudent.get(String(st.id)) || { exams: 0, obtained: 0, total: 0 };
        mine.exams += 1; mine.obtained += got; mine.total += outOf;
        perStudent.set(String(st.id), mine);
      });
      perSection.set(String(c.id), sec);
    });
    report.sat = report.passed + report.failed;
    report.passPct = pct1(report.passed, report.sat);
    report.avgPct = pct1(obtained, report.sat * outOf);
    if (report.sections > 0) examRows.push(report);
  });
  examRows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

  // --- Sections --------------------------------------------------------------
  const bySection: SectionReport[] = sections.map((c) => {
    const id = String(c.id);
    const own = sheets.filter((w) => w.classId === id)
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    const exam = perSection.get(id) || { passed: 0, sat: 0, obtained: 0, total: 0 };
    return {
      id,
      label: c.label,
      gradeName: c.gradeName || c.label,
      students: rollOf(id).length,
      teachers: teachers.filter((x) => x.classId === id).map((x) => x.name).filter(Boolean).join(', '),
      registers: own.length,
      lastRegister: own[0]?.date,
      attendance: classAttendance(sheets, id),
      offerings: sumOf(offerings.filter((o) => o.classId === id), (o) => o.offering),
      results: { passed: exam.passed, sat: exam.sat, passPct: pct1(exam.passed, exam.sat) },
      avgPct: pct1(exam.obtained, exam.total)
    };
  });

  const gradeMap = new Map<string, GradeReport>();
  bySection.forEach((s) => {
    const g = gradeMap.get(s.gradeName) || { name: s.gradeName, sections: 0, students: 0, withoutTeacher: 0 };
    g.sections += 1;
    g.students += s.students;
    if (!s.teachers) g.withoutTeacher += 1;
    gradeMap.set(s.gradeName, g);
  });
  const byGrade = Array.from(gradeMap.values())
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  // --- Attendance ------------------------------------------------------------
  const sunday = lastSunday(asOf);
  const sundaysBack = (from: number) => [0, 1, 2, 3].map((w) => shiftSunday(sunday, -(from + w)));
  const last4Sundays = sundaysBack(0);
  const prev4Sundays = sundaysBack(4);
  const onDates = (dates: string[]) => sheets.filter((w) => dates.includes(String(dateOf(w.date))));

  const attendanceMonths: AttendanceMonth[] = lastMonths(sheets, (w) => w.date, 6, asOf).map((b) => {
    const rate = overallAttendance(sheets.filter((w) => String(w.date || '').startsWith(b.month)));
    return { ...b, present: rate.present, marked: rate.marked, pct: rate.pct };
  });

  const byStudent: StudentAttendanceRow[] = students
    .map((st) => ({
      id: String(st.id || ''),
      name: st.name || '-',
      className: sectionById.get(String(st.classId))?.label || 'Unassigned',
      parentName: st.parentName,
      phone: st.phone,
      attendance: studentAttendance(sheets, String(st.id))
    }))
    .filter((r) => r.attendance.marked > 0)
    .sort((a, b) => (a.attendance.pct ?? 0) - (b.attendance.pct ?? 0) || a.name.localeCompare(b.name));

  // --- Offerings -------------------------------------------------------------
  const termStart = dateOf(term?.startDate);
  const termEndIso = dateOf(term?.endDate);
  const termRows = termStart || termEndIso
    ? offerings.filter((o) => {
      const d = dateOf(o.date);
      return d !== null && (!termStart || d >= termStart) && (!termEndIso || d <= termEndIso);
    })
    : offerings.filter((o) => (dateOf(o.date) || '').startsWith(`${now.getFullYear()}-`));

  const examPassed = examRows.reduce((n, x) => n + x.passed, 0);
  const examSat = examRows.reduce((n, x) => n + x.sat, 0);
  let allObtained = 0; let allTotal = 0;
  perSection.forEach((s) => { allObtained += s.obtained; allTotal += s.total; });

  return {
    students: students.length,
    sections: sections.length,
    teachers: teachers.length,
    sectionsWithoutTeacher: bySection.filter((s) => !s.teachers),
    overall: overallAttendance(sheets),
    last4Sundays,
    last4: overallAttendance(onDates(last4Sundays)),
    prev4: overallAttendance(onDates(prev4Sundays)),
    lastSunday: sunday,
    notMarked: bySection.filter((s) => s.students > 0
      && !sheets.some((w) => w.classId === s.id && dateOf(w.date) === sunday)),
    attendanceMonths,
    byGrade,
    bySection,
    byStudent,
    lowAttendance: byStudent.filter((r) => (r.attendance.pct ?? 100) < LOW_ATTENDANCE_PCT),
    offeringsTotal: sumOf(offerings, (o) => o.offering),
    offeringsThisMonth: sumOf(inMonth(offerings, (o) => o.date, now), (o) => o.offering),
    offeringsTerm: sumOf(termRows, (o) => o.offering),
    termLabel: term?.name || String(now.getFullYear()),
    offeringMonths: lastMonths(offerings, (o) => o.date, 12, asOf, (o) => o.offering),
    classTotals: sumOf(offerings.filter((o) => !o.studentId), (o) => o.offering),
    personalGifts: sumOf(offerings.filter((o) => o.studentId), (o) => o.offering),
    exams: examRows,
    examPassed,
    examSat,
    examPassPct: pct1(examPassed, examSat),
    examAvgPct: pct1(allObtained, allTotal),
    topScorers: Array.from(perStudent.entries())
      .filter(([, s]) => s.total > 0)
      .map(([id, s]) => {
        const st = students.find((x) => x.id === id);
        return {
          id, name: st?.name || id, className: sectionById.get(String(st?.classId))?.label || '-',
          exams: s.exams, obtained: s.obtained, total: s.total, pct: pct1(s.obtained, s.total) as number
        };
      })
      .sort((a, b) => b.pct - a.pct || b.exams - a.exams || a.name.localeCompare(b.name))
      .slice(0, 5)
  };
};

/**
 * Students on the roll who sit in no live section — never enrolled, or left
 * behind when their section was deleted. They drop out of every other figure,
 * so they are listed on their own.
 */
export const studentsWithoutSection = <S extends SsStudentLike>(students: S[], liveClassIds: Array<string | undefined>): S[] => {
  const live = new Set(liveClassIds.filter(Boolean).map(String));
  return students.filter((st) => !st.classId || !live.has(String(st.classId)))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
};

/** Change in attendance between two windows, in percentage points; null when either is blank. */
export const attendanceTrend = (current: AttendanceRate, previous: AttendanceRate): number | null =>
  current.pct === null || previous.pct === null ? null : Math.round((current.pct - previous.pct) * 10) / 10;
